/**
 * PDF Image Handler Utility
 * Handles image processing and rendering for PDF reports
 */

/**
 * Organizes images by type and prepares them for rendering in the PDF
 * @param {Array} images - Array of image objects from the API
 * @returns {Object} Organized images with metadata
 */
const organizeImages = (images) => {
  if (!images || !Array.isArray(images) || images.length === 0) {
    return { hasImages: false };
  }

  // Group images by their type
  const imagesByType = {};
  const imageTypes = [
    'front_windscreen',
    'front_left_corner',
    'front_right_corner',
    'rear_left_corner',
    'rear_right_corner',
    'odometer',
    'chassis_number'
  ];

  // Initialize with empty arrays
  imageTypes.forEach(type => {
    imagesByType[type] = [];
  });

  // Organize images by their type
  images.forEach(img => {
    if (img.photoType && imagesByType.hasOwnProperty(img.photoType)) {
      // Format the image data for PDF rendering
      imagesByType[img.photoType].push({
        url: img.imageUrl,
        type: img.photoType,
        capturedAt: img.capturedAt ? new Date(img.capturedAt).toLocaleDateString() : 'Unknown',
        remarks: img.remarks || '',
        data: null, // Will be populated with image data if fetched
        src: img.imageUrl, // URL for jsPDF to load
        format: 'JPEG' // Default format
      });
    }
  });

  return {
    hasImages: images.length > 0,
    imagesByType,
    allImages: images,
    count: images.length
  };
};

/**
 * Renders images in the PDF document
 * @param {Object} doc - jsPDF document instance
 * @param {Array} images - Array of image objects from the API
 * @param {Number} startY - Starting Y position for rendering
 * @param {Number} pageWidth - Width of the PDF page
 * @param {Object} margins - Page margins object
 * @returns {Number} The Y position after rendering images
 */
const renderImagesToPdf = (doc, images, startY, pageWidth, margins) => {
  if (!images || !Array.isArray(images) || images.length === 0) {
    return startY;
  }

  const organizedImages = organizeImages(images);
  if (!organizedImages.hasImages) {
    return startY;
  }

  // Set up image rendering parameters
  const contentWidth = pageWidth - margins.left - margins.right;
  const maxImagesPerRow = 2;
  const imageWidth = (contentWidth / maxImagesPerRow) - 15;
  const imageHeight = 120;
  const imageSpacing = 15;
  
  let currentY = startY + 15;
  let currentX = margins.left;
  let imagesInCurrentRow = 0;
  
  // Title for the images section
  doc.setFontSize(14);
  doc.setTextColor('#1E3A8A'); // Primary color
  doc.setFont('helvetica', 'bold');
  doc.text('Inspection Images', margins.left, startY);
  
  // Helper function to format image type for display
  const formatImageType = (type) => {
    return type
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Render each image
  let imageCount = 0;
  for (const type in organizedImages.imagesByType) {
    const typeImages = organizedImages.imagesByType[type];
    
    for (const img of typeImages) {
      try {
        // Check if we need to move to the next row
        if (imagesInCurrentRow >= maxImagesPerRow) {
          currentY += imageHeight + imageSpacing;
          currentX = margins.left;
          imagesInCurrentRow = 0;
        }
        
        // Check if we need to move to a new page
        if (currentY + imageHeight > doc.internal.pageSize.getHeight() - 50) {
          doc.addPage();
          currentY = margins.left;
          currentX = margins.left;
          imagesInCurrentRow = 0;
        }
        
        // Add the image
        doc.addImage(
          img.src,
          img.format,
          currentX,
          currentY,
          imageWidth,
          imageHeight,
          `img_${imageCount}`,
          'FAST'
        );
        
        // Add image caption
        const captionY = currentY + imageHeight + 5;
        doc.setFontSize(9);
        doc.setTextColor('#4B5563'); // Secondary text color
        doc.setFont('helvetica', 'bold');
        doc.text(formatImageType(img.type), currentX, captionY);
        
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.text(`Captured: ${img.capturedAt}`, currentX, captionY + 10);
        
        // Move to the next position
        currentX += imageWidth + imageSpacing;
        imagesInCurrentRow++;
        imageCount++;
      } catch (error) {
        console.warn(`Failed to add image (${img.type}):`, error);
      }
    }
  }
  
  // Return the Y position after all images
  return currentY + imageHeight + 30;
};

/**
 * Handles errors when loading images
 * @param {Object} doc - jsPDF document instance
 * @param {Number} y - Y position to display error message
 * @param {Object} margins - Page margins object
 * @returns {Number} Updated Y position
 */
const handleImageError = (doc, y, margins) => {
  doc.setFontSize(10);
  doc.setTextColor('#EF4444'); // Red color for error
  doc.setFont('helvetica', 'italic');
  doc.text('* Some images could not be loaded.', margins.left, y + 10);
  return y + 20;
};

const pdfImageHandler = {
  organizeImages,
  renderImagesToPdf,
  handleImageError
};

export default pdfImageHandler;
